import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Sun, Check, Star, TrendingUp, Zap, DollarSign, Loader2 } from 'lucide-react';
import { MobileVerification } from './MobileVerification';
import axios from 'axios';
import { trackInitiateCheckout } from '../utils/fbPixel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Calculate solar recommendations based on monthly bill and available pricing
const calculateSolarOptions = (monthlyBill, solarPrices) => {
  // Base calculations on monthly electricity bill
  const annualBill = monthlyBill * 12;
  
  // Get available sizes from pricing, sorted
  const availableSizes = solarPrices.map(p => p.size_kw).sort((a, b) => a - b);
  
  // Helper to get pricing for a size
  const getPricingForSize = (size) => {
    const pricing = solarPrices.find(p => p.size_kw === size);
    if (pricing) {
      return {
        wasPrice: pricing.was_price || pricing.price + 2000,
        nowPrice: pricing.now_price || pricing.price,
      };
    }
    // Fallback calculation
    return {
      wasPrice: size * 1200,
      nowPrice: size * 900,
    };
  };
  
  // If no pricing data, use defaults
  if (availableSizes.length === 0) {
    return [
      {
        tier: 'good',
        name: 'Good Savings',
        description: 'Entry-level system for budget-conscious households',
        systemSizeMin: 3,
        systemSizeMax: 4,
        wasPrice: 5500,
        nowPrice: 3500,
        savingsPercent: 0.50,
        annualSavings: Math.round(annualBill * 0.50),
        recommended: false,
      },
      {
        tier: 'best',
        name: 'Best Value',
        description: 'Optimal balance of upfront cost and long-term savings',
        systemSizeMin: 5,
        systemSizeMax: 7,
        wasPrice: 8500,
        nowPrice: 6500,
        savingsPercent: 0.65,
        annualSavings: Math.round(annualBill * 0.65),
        recommended: true,
      },
      {
        tier: 'max',
        name: 'Maximum Savings',
        description: 'Larger system for households wanting maximum returns',
        systemSizeMin: 8,
        systemSizeMax: 10,
        wasPrice: 12000,
        nowPrice: 9500,
        savingsPercent: 0.80,
        annualSavings: Math.round(annualBill * 0.80),
        recommended: false,
      },
    ];
  }
  
  // Calculate ideal system size based on bill
  // Rule of thumb: ~1kW per $25-30 monthly bill
  const idealSize = Math.round(monthlyBill / 27);
  
  // Find closest available sizes for each tier
  const findClosestSize = (target, minIdx = 0, maxIdx = availableSizes.length - 1) => {
    const validSizes = availableSizes.slice(minIdx, maxIdx + 1);
    return validSizes.reduce((prev, curr) => 
      Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev
    );
  };
  
  // Good Savings - smaller system (60-80% of ideal)
  const goodMin = findClosestSize(Math.max(availableSizes[0], idealSize * 0.6), 0, Math.floor(availableSizes.length / 2));
  const goodMax = findClosestSize(Math.max(availableSizes[0], idealSize * 0.8), 0, Math.floor(availableSizes.length / 2));
  const goodPricing = getPricingForSize(Math.max(goodMin, goodMax));
  
  // Best Value - optimal size (90-110% of ideal)
  const bestMin = findClosestSize(idealSize * 0.9);
  const bestMax = findClosestSize(idealSize * 1.1);
  const bestPricing = getPricingForSize(Math.max(bestMin, bestMax));
  
  // Maximum Savings - larger system (120-150% of ideal)
  const maxMin = findClosestSize(idealSize * 1.2, Math.floor(availableSizes.length / 2));
  const maxMax = findClosestSize(Math.min(availableSizes[availableSizes.length - 1], idealSize * 1.5), Math.floor(availableSizes.length / 2));
  const maxPricing = getPricingForSize(Math.max(maxMin, maxMax));
  
  return [
    {
      tier: 'good',
      name: 'Good Savings',
      description: 'Entry-level system for budget-conscious households',
      systemSizeMin: Math.min(goodMin, goodMax),
      systemSizeMax: Math.max(goodMin, goodMax),
      wasPrice: goodPricing.wasPrice,
      nowPrice: goodPricing.nowPrice,
      savingsPercent: 0.50,
      annualSavings: Math.round(annualBill * 0.50),
      recommended: false,
    },
    {
      tier: 'best',
      name: 'Best Value',
      description: 'Optimal balance of upfront cost and long-term savings',
      systemSizeMin: Math.min(bestMin, bestMax),
      systemSizeMax: Math.max(bestMin, bestMax),
      wasPrice: bestPricing.wasPrice,
      nowPrice: bestPricing.nowPrice,
      savingsPercent: 0.65,
      annualSavings: Math.round(annualBill * 0.65),
      recommended: true,
    },
    {
      tier: 'max',
      name: 'Maximum Savings',
      description: 'Larger system for households wanting maximum returns',
      systemSizeMin: Math.min(maxMin, maxMax),
      systemSizeMax: Math.max(maxMin, maxMax),
      wasPrice: maxPricing.wasPrice,
      nowPrice: maxPricing.nowPrice,
      savingsPercent: 0.80,
      annualSavings: Math.round(annualBill * 0.80),
      recommended: false,
    },
  ];
};

export const SolarResults = ({ monthlyBill, postcode, enquiryId, onGetQuotes, onBack }) => {
  const [showContactForm, setShowContactForm] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [solarPrices, setSolarPrices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch pricing data on mount
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/admin/pricing`);
        setSolarPrices(response.data.solar_pricing || []);
      } catch (err) {
        console.error('Error fetching pricing:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPricing();
  }, []);

  const options = calculateSolarOptions(monthlyBill, solarPrices);
  
  const handleGetQuotes = (option) => {
    // Track InitiateCheckout in Facebook Pixel
    trackInitiateCheckout('solar', option.tier);
    setSelectedOption(option);
    setShowContactForm(true);
  };

  // Show contact form
  if (showContactForm) {
    return (
      <MobileVerification
        enquiryId={enquiryId}
        selectedOption={selectedOption}
        productType="solar"
        onBack={() => setShowContactForm(false)}
        onVerified={() => {
          if (onGetQuotes) {
            onGetQuotes(selectedOption);
          }
        }}
      />
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1.5 rounded-full text-xs font-medium mb-2">
          <Check className="w-3 h-3" />
          Based on your ${monthlyBill}/month electricity bill
        </div>
        <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-1">
          Your Solar Options
        </h2>
        <p className="text-gray-500 text-sm">
          3 tailored options for postcode {postcode}
        </p>
      </div>

      {/* Options Grid */}
      <div className="grid md:grid-cols-3 gap-3">
        {options.map((option) => {
          return (
            <Card 
              key={option.tier}
              className={`relative overflow-hidden transition-all duration-300 hover:shadow-xl ${
                option.recommended 
                  ? 'border-2 border-cyo-orange shadow-lg scale-[1.01] md:scale-[1.03]' 
                  : 'border border-gray-200 hover:border-gray-300'
              }`}
            >
              {/* Recommended Badge */}
              {option.recommended && (
                <div className="absolute top-0 left-0 right-0 bg-cyo-orange text-white text-center py-1 text-xs font-semibold flex items-center justify-center gap-1">
                  <Star className="w-3 h-3 fill-white" />
                  Recommended
                </div>
              )}
              
              <CardContent className={`p-4 ${option.recommended ? 'pt-8' : ''}`}>
                {/* Option Name */}
                <div className="flex items-center gap-2 mb-2">
                  {option.tier === 'good' && <TrendingUp className="w-4 h-4 text-emerald-500" />}
                  {option.tier === 'best' && <Zap className="w-4 h-4 text-cyo-orange" />}
                  {option.tier === 'max' && <Sun className="w-4 h-4 text-blue-500" />}
                  <h3 className={`text-base font-bold ${
                    option.recommended ? 'text-cyo-orange' : 'text-gray-900'
                  }`}>
                    {option.name}
                  </h3>
                </div>
                
                {/* Description */}
                <p className="text-xs text-gray-500 mb-3">
                  {option.description}
                </p>
                
                {/* System Size */}
                <div className="bg-gray-50 rounded-lg p-3 mb-3">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">System Size</p>
                  <p className="text-xl font-bold text-gray-900">
                    {option.systemSizeMin} - {option.systemSizeMax} <span className="text-sm font-medium text-gray-500">kW</span>
                  </p>
                </div>
                
                {/* Annual Savings */}
                <div className={`rounded-lg p-3 mb-3 ${
                  option.recommended 
                    ? 'bg-orange-50 border border-orange-100' 
                    : 'bg-green-50'
                }`}>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">Expected Annual Savings</p>
                  <p className={`text-2xl font-bold ${
                    option.recommended ? 'text-cyo-orange' : 'text-green-600'
                  }`}>
                    ${option.annualSavings.toLocaleString()}
                    <span className="text-xs font-medium text-gray-500">/year</span>
                  </p>
                  <p className="text-[10px] text-gray-400">
                    ~{Math.round(option.savingsPercent * 100)}% reduction in bills
                  </p>
                </div>
                
                {/* CTA Button */}
                <Button 
                  onClick={() => handleGetQuotes(option)}
                  className={`w-full font-semibold py-4 rounded-lg transition-all ${
                    option.recommended 
                      ? 'bg-cyo-orange hover:bg-cyo-orange-dark text-white shadow-lg hover:shadow-xl' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                  style={option.recommended ? {
                    animation: 'flash-attention 1.5s ease-in-out infinite',
                    backgroundColor: '#F7941D',
                  } : {}}
                >
                  <DollarSign className="w-4 h-4 mr-1" />
                  See My Pricing
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Back button */}
      <div className="text-center">
        <Button 
          variant="ghost" 
          onClick={onBack}
          className="text-gray-500 hover:text-gray-700 text-sm py-1"
        >
          ← Start a new comparison
        </Button>
      </div>
    </div>
  );
};
